import { useFormik } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RequestWithdrawalResult,
  useCancelWithdrawalMutation,
  useRequestWithdrawalMutation,
  useUpdateAccountAssetMutation,
} from 'views/utils/graphql/graphql';
import { useWithdrawModal } from './Modal/WithDrawModalHook';
import { useToast } from 'views/components/atoms/toast/hook';
import { ToastMessageLevel } from 'views/components/atoms/toast';
import { formatNumberForInt } from 'views/utils/format/FormatNumber';
import { toHankaku } from 'views/utils/format/FormatString';
import { formatDateToSlashDateTime } from 'views/utils/format/FormatDate';

type AccountAsset = {
  DepositAmount: number;
  MarginAmount: number;
  WithdrawableAmount: number;
  TotalAssets: number;
  UpdTime: string;
  WithdrawalReservation?: number;
  WithdrawalCommission?: number;
  WithdrawalDay?: string;
  RefundAmount?: number;
  RefundCommission?: number;
  RefundDay?: string;
};

type WithdrawAmountForm = {
  withdrawAmount: string;
};

type WithdrawalConfirmAction = 'request' | 'cancel';

const nullToUndefined = <T>(value: T | null | undefined) => value ?? undefined;
const toOptionalNumber = (value: string | null | undefined) => (value == null ? undefined : Number(value));
const formatWithdrawAmount = (amount: number | undefined) => (amount === undefined ? '' : formatNumberForInt(amount));
const normalizeWithdrawAmountText = (amount: string) => toHankaku(amount.trim()).replace(/,/g, '');
const parseWithdrawAmount = (amount: string) => {
  const normalizedAmount = normalizeWithdrawAmountText(amount);
  if (normalizedAmount === '') return undefined;
  if (!/^\d+$/.test(normalizedAmount)) return undefined;

  const parsedAmount = Number(normalizedAmount);
  return Number.isNaN(parsedAmount) ? undefined : parsedAmount;
};

const initialAccountAsset: AccountAsset = {
  DepositAmount: 0,
  MarginAmount: 0,
  WithdrawableAmount: 0,
  TotalAssets: 0,
  UpdTime: '',
  WithdrawalReservation: 0,
  WithdrawalCommission: undefined,
  WithdrawalDay: undefined,
  RefundAmount: undefined,
  RefundCommission: undefined,
  RefundDay: undefined,
};

export const useAccountAsset = () => {
  const [updateAccountAssetResult, updateAccountAsset] = useUpdateAccountAssetMutation();
  const [requestWithdrawalResult, requestWithdrawal] = useRequestWithdrawalMutation();
  const [cancelWithdrawalResult, cancelWithdrawal] = useCancelWithdrawalMutation();

  const [accountAsset, setAccountAsset] = useState(initialAccountAsset);
  const shouldSyncWithdrawAmountRef = useRef(true);
  const [withdrawalCheckResult, setWithdrawalCheckResult] = useState<RequestWithdrawalResult | undefined>();
  const [withdrawalRequestCompleteResult, setWithdrawalRequestCompleteResult] = useState<
    RequestWithdrawalResult | undefined
  >();
  const [isWithdrawalRequestCompleteModalOpen, setWithdrawalRequestCompleteModalOpen] = useState(false);
  const [withdrawalConfirmAction, setWithdrawalConfirmAction] = useState<WithdrawalConfirmAction>('request');
  const { toastProps, showToast } = useToast();
  const { modalProps, showModal } = useWithdrawModal(
    nullToUndefined(withdrawalCheckResult?.PrevRequestedWithdrawalAmount),
    withdrawalCheckResult?.RequestedWithdrawalAmount,
    withdrawalCheckResult?.WithdrawalDate,
    withdrawalCheckResult?.Commission,
    withdrawalCheckResult?.WithdrawalAmount,
  );

  const refreshAccountAsset = useCallback(
    (syncWithdrawAmount = false) => {
      if (syncWithdrawAmount) {
        shouldSyncWithdrawAmountRef.current = true;
      }

      updateAccountAsset({});
    },
    [updateAccountAsset],
  );

  useEffect(() => {
    refreshAccountAsset();
  }, [refreshAccountAsset]);

  const validate = useCallback(
    (values: WithdrawAmountForm) => {
      const errors: Partial<WithdrawAmountForm> = {};
      const normalizedAmount = normalizeWithdrawAmountText(values.withdrawAmount);
      const requestAmount = parseWithdrawAmount(values.withdrawAmount);

      if (normalizedAmount === '') {
        errors.withdrawAmount = '出金予約額を入力してください';
      } else if (!/^\d+$/.test(normalizedAmount)) {
        errors.withdrawAmount = '出金予約額は数字で入力してください';
      } else if (requestAmount === undefined) {
        errors.withdrawAmount = '出金予約額を入力してください';
      } else if (requestAmount > accountAsset.DepositAmount - (accountAsset.RefundAmount ?? 0)) {
        errors.withdrawAmount = '出金予約額は預り金－出金予定額（未配分返金）以下で入力してください';
      }

      return errors;
    },
    [accountAsset.DepositAmount, accountAsset.RefundAmount],
  );

  const formik = useFormik<WithdrawAmountForm>({
    initialValues: {
      withdrawAmount: formatWithdrawAmount(initialAccountAsset.WithdrawalReservation ?? 0),
    },
    onSubmit: async (values) => {
      const requestAmount = parseWithdrawAmount(values.withdrawAmount);
      if (requestAmount === undefined) return;

      const result = await requestWithdrawal({
        input: {
          Amount: String(requestAmount),
          CheckMode: true,
        },
      });

      const withdrawalResult = result.data?.RequestWithdrawal;
      if (!withdrawalResult) return;

      if (withdrawalResult.Code !== 0) {
        showToast(withdrawalResult.Message ?? '出金予約に失敗しました', ToastMessageLevel.Error);
        return;
      }

      setWithdrawalCheckResult(withdrawalResult);
      setWithdrawalConfirmAction('request');
      showModal(true);
    },
    validate,
    validateOnChange: true,
    validateOnBlur: false,
  });
  const { errors, submitCount, touched, values, setFieldTouched, setFieldValue, submitForm } = formik;

  useEffect(() => {
    const data = updateAccountAssetResult.data?.UpdateAccountAsset;
    if (!data) return;

    // DateTime型の未設定値(0001-01-01等)は'ー'として扱う
    const updTimeDate = new Date(data.UpdTime);
    const updTime =
      Number.isNaN(updTimeDate.getTime()) || updTimeDate.getFullYear() <= 1
        ? 'ー'
        : formatDateToSlashDateTime(data.UpdTime);

    const nextAccountAsset = {
      DepositAmount: Number(data.DepositAmount),
      MarginAmount: Number(data.MarginAmount),
      WithdrawableAmount: Number(data.WithdrawableAmount),
      TotalAssets: Number(data.TotalAssets),
      UpdTime: updTime,
      WithdrawalReservation: toOptionalNumber(data.WithdrawalReservation),
      WithdrawalCommission: toOptionalNumber(data.WithdrawalCommission),
      WithdrawalDay: nullToUndefined(data.WithdrawalDay),
      RefundAmount: toOptionalNumber(data.RefundAmount),
      RefundCommission: toOptionalNumber(data.RefundCommission),
      RefundDay: nullToUndefined(data.RefundDay),
    };

    setAccountAsset(nextAccountAsset);
    if (shouldSyncWithdrawAmountRef.current) {
      setFieldValue('withdrawAmount', formatWithdrawAmount(nextAccountAsset.WithdrawalReservation ?? 0), false);
      shouldSyncWithdrawAmountRef.current = false;
    }
  }, [setFieldValue, updateAccountAssetResult.data]);

  const withdrawAmount = values.withdrawAmount;
  const setWithdrawAmount = useCallback(
    (value: React.SetStateAction<string>) => {
      const nextValue = typeof value === 'function' ? value(values.withdrawAmount) : value;
      setFieldValue('withdrawAmount', nextValue);
    },
    [setFieldValue, values.withdrawAmount],
  );
  const onWithdrawAmountBlur = useCallback(() => {
    setFieldTouched('withdrawAmount', true);
  }, [setFieldTouched]);

  const closeWithdrawalRequestCompleteModal = useCallback(() => {
    setWithdrawalRequestCompleteModalOpen(false);
    refreshAccountAsset(true);
  }, [refreshAccountAsset]);

  const reservationList = [
    {
      type: '出金予約',
      reservation: accountAsset.WithdrawalReservation,
      commission: accountAsset.WithdrawalCommission,
      withdrawDate: accountAsset.WithdrawalDay,
    },
    {
      type: '未配分返金',
      reservation: accountAsset.RefundAmount,
      commission: accountAsset.RefundCommission,
      withdrawDate: accountAsset.RefundDay,
    },
  ];
  const requestWithdrawalConfirm = useCallback(async () => {
    const requestAmount = parseWithdrawAmount(withdrawAmount);
    if (requestAmount === undefined) return;

    const result = await requestWithdrawal({
      input: {
        Amount: String(requestAmount),
        CheckMode: false,
      },
    });

    const withdrawalResult = result.data?.RequestWithdrawal;
    if (!withdrawalResult) return;

    if (withdrawalResult.Code !== 0) {
      showToast(withdrawalResult.Message ?? '出金予約に失敗しました', ToastMessageLevel.Error);
      return;
    }

    setWithdrawalRequestCompleteResult(withdrawalResult);
    showModal(false);
    setWithdrawalRequestCompleteModalOpen(true);
  }, [requestWithdrawal, showModal, showToast, withdrawAmount]);

  const cancelWithdrawalCheck = useCallback(async () => {
    const result = await cancelWithdrawal({
      input: {
        CheckMode: true,
      },
    });

    const withdrawalResult = result.data?.CancelWithdrawal;
    if (!withdrawalResult) return;

    if (withdrawalResult.Code !== 0) {
      showToast(withdrawalResult.Message ?? '出金予約取消に失敗しました', ToastMessageLevel.Error);
      return;
    }

    setWithdrawalCheckResult(withdrawalResult);
    setWithdrawalConfirmAction('cancel');
    showModal(true);
  }, [cancelWithdrawal, showModal, showToast]);

  const cancelWithdrawalConfirm = useCallback(async () => {
    const result = await cancelWithdrawal({
      input: {
        CheckMode: false,
      },
    });

    const withdrawalResult = result.data?.CancelWithdrawal;
    if (!withdrawalResult) return;

    if (withdrawalResult.Code !== 0) {
      showToast(withdrawalResult.Message ?? '出金予約取消に失敗しました', ToastMessageLevel.Error);
      return;
    }

    showModal(false);
    refreshAccountAsset(true);
  }, [cancelWithdrawal, refreshAccountAsset, showModal, showToast]);

  const isWithdrawalConfirmModalOpen = modalProps.isOpen && withdrawalConfirmAction === 'request';
  const isWithdrawalCancelModalOpen = modalProps.isOpen && withdrawalConfirmAction === 'cancel';

  return {
    accountAsset,
    reservationList,
    modalProps: {
      ...modalProps,
      isOpen: isWithdrawalConfirmModalOpen,
      onConfirm: requestWithdrawalConfirm,
      disabled: requestWithdrawalResult.fetching || cancelWithdrawalResult.fetching,
    },
    withdrawalCancelModalProps: {
      ...modalProps,
      isOpen: isWithdrawalCancelModalOpen,
      withdrawAmount: nullToUndefined(withdrawalCheckResult?.PrevRequestedWithdrawalAmount),
      onConfirm: cancelWithdrawalConfirm,
      disabled: requestWithdrawalResult.fetching || cancelWithdrawalResult.fetching,
    },
    withdrawalRequestCompleteModalProps: {
      isOpen: isWithdrawalRequestCompleteModalOpen,
      requestedWithdrawalAmount: withdrawalRequestCompleteResult?.RequestedWithdrawalAmount,
      withdrawalDate: withdrawalRequestCompleteResult?.WithdrawalDate,
      onClose: closeWithdrawalRequestCompleteModal,
    },
    showModal,
    withdrawAmount,
    setWithdrawAmount,
    withdrawAmountError: errors.withdrawAmount,
    isWithdrawAmountError: (!!touched.withdrawAmount || submitCount > 0) && !!errors.withdrawAmount,
    onWithdrawAmountBlur,
    requestWithdrawalCheck: submitForm,
    isRequestingWithdrawal: requestWithdrawalResult.fetching,
    cancelWithdrawalCheck,
    isCancelingWithdrawal: cancelWithdrawalResult.fetching,
    refreshAccountAsset,
    isUpdatingAccountAsset: updateAccountAssetResult.fetching,
    toastProps,
  };
};
